/**
 * Vincent B823 (Eric 2026-02-13) — Admin Embeddings & RAG.
 *
 * Page admin qui :
 *   1. Affiche les stats globales d'embeddings (`/api/admin/embeddings-stats`)
 *   2. Permet de diagnostiquer une vidéo précise (`?video_id=X`)
 *   3. Lance un backfill global (`?force_all=true|false`)
 *
 * Contexte : après le fix B823 (embedding généré à l'approbation), il reste
 * un stock HISTORIQUE de KUs `status=approved` sans embedding (les 6 pipelines
 * ne les créaient pas — bug initial). Cette page permet de rattraper ce
 * stock en un clic (Vincent : « bouton global pour réindexer tout le LMS »).
 *
 * Route : `/dashboard/admin/embeddings` (founder-only)
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../../contexts/LanguageContext";
import { API_BASE as API } from "../../lib/apiBase";
import { confirmDialog } from "../../lib/uiDialogs";
import { Cube, MagnifyingGlass, ArrowsClockwise, Warning, CheckCircle, ChartLine } from "@phosphor-icons/react";
import { trHorsContexte } from "../../lib/langHorsContexte";

const STAT_TONE = {
  good: "bg-emerald-50 border-emerald-200 text-emerald-700",
  warn: "bg-amber-50 border-amber-200 text-amber-700",
  bad: "bg-rose-50 border-rose-200 text-rose-700",
  default: "bg-white border-zinc-200 text-zinc-700",
};

function StatCard({ label, value, tone = "default", testid }) {
  const cls = STAT_TONE[tone] || STAT_TONE.default;
  return (
    <div className={`rounded-lg border ${cls} px-3 py-2`} data-testid={`emb-stat-${testid || String(label).toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wide font-bold opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

// Vincent B987 (Eric 2026-02-26 SPEC STRICK PROD) — Bannière d'état du
// cron cron-job.org `embedding-health-check`. 3 états :
//   - vert : dernier run < 26h (marge sur les 24h attendues)
//   - ambre : dernier run entre 26h et 48h (une exécution ratée)
//   - rouge : dernier run > 48h OU aucun heartbeat (jamais tourné)
function CronHeartbeatBanner({ heartbeat, lang }) {
  const en = lang === "en";
  if (!heartbeat || !heartbeat.last_run_at) {
    return (
      <div
        className="rounded-lg border border-rose-300 bg-rose-50 text-rose-800 px-4 py-2.5 text-sm flex items-start gap-2"
        data-testid="cron-heartbeat-banner"
        data-status="never"
      >
        <Warning size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-semibold">
            {en ? "Cron never ran" : "Cron jamais exécuté"}
          </div>
          <div className="text-xs opacity-90">
            {en
              ? "Configure the daily cron-job.org task pointing to POST /api/cron/embedding-health-check (see docs/CRON_SETUP.md, Job 4)."
              : "Configurez la tâche quotidienne cron-job.org pointant vers POST /api/cron/embedding-health-check (voir docs/CRON_SETUP.md, Job 4)."}
          </div>
        </div>
      </div>
    );
  }
  const lastMs = new Date(heartbeat.last_run_at).getTime();
  const ageH = (Date.now() - lastMs) / 3_600_000;
  let tone = "ok";
  if (ageH >= 48) tone = "bad";
  else if (ageH >= 26) tone = "warn";
  const cls = {
    ok: "border-emerald-300 bg-emerald-50 text-emerald-800",
    warn: "border-amber-300 bg-amber-50 text-amber-800",
    bad: "border-rose-300 bg-rose-50 text-rose-800",
  }[tone];
  const Icon = tone === "ok" ? CheckCircle : Warning;
  const localised = new Date(heartbeat.last_run_at).toLocaleString(en ? "en-US" : "fr-FR");
  return (
    <div
      className={`rounded-lg border ${cls} px-4 py-2.5 text-sm flex items-start gap-2`}
      data-testid="cron-heartbeat-banner"
      data-status={tone}
    >
      <Icon size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <div className="font-semibold">
          {en
            ? `Cron embedding-health-check ${tone === "ok" ? "healthy" : tone === "warn" ? "delayed" : "stale"}`
            : `Cron embedding-health-check ${tone === "ok" ? "sain" : tone === "warn" ? "en retard" : "obsolète"}`}
        </div>
        <div className="text-xs opacity-90">
          {en ? "Last run: " : "Dernière exécution : "}
          <span className="font-mono" data-testid="cron-heartbeat-last-run">{localised}</span>
          {" · "}
          <span data-testid="cron-heartbeat-age-hours">{ageH.toFixed(1)}h</span>
          {heartbeat.last_result_summary && (
            <>
              {" · "}
              {en ? "missing " : "manquants "}
              <span className="font-mono">{heartbeat.last_result_summary.combined_missing ?? heartbeat.last_result_summary.missing_count ?? 0}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Vincent B825 (Eric 2026-02-13) — Mini-chart SVG pur (pas de lib externe).
// Affiche 2 courbes principales : `missing_count` (vermillon plein) +
// `visible_count` (bleu plein) pour les knowledge_units.
// Vincent B829 v2 follow-up (Eric 2026-02-14 SPEC STRICK PROD) — Ajoute
// 2 courbes en POINTILLÉS : `scripts_missing` (vermillon dash) + `scripts_visible`
// (bleu dash) pour la collection sales_scripts (désormais indexée par RAG).
// Même échelle Y, séries superposées → l'admin voit d'un coup d'œil si
// la santé se dégrade sur l'une OU l'autre collection.
// Range Y auto sur max(total_approved + scripts_total).
// Vincent B845 (Eric 2026-02-14 SPEC STRICK PROD) — Palette Wong (Nature
// Methods 2011) daltonien-safe. Bleu `#0072B2` + vermillon `#D55E00`
// restent distinguables par les 3 types de daltonisme (deutéranopie,
// protanopie, tritanopie). Événements : orange `#E69F00`, mauve `#CC79A7`,
// vert-bleu `#009E73` — tous distincts sur simulateurs Coblis.
const CB = {
  visible: "#0072B2",       // bleu Wong — courbes "OK" (KUs + Scripts)
  missing: "#D55E00",       // vermillon Wong — courbes "à réindexer"
  missingArea: "#fde3cf",   // vermillon très clair pour l'aire sous la courbe
  eventBackfill: "#E69F00", // orange Wong — B (backfill global)
  eventReindex: "#CC79A7",  // mauve Wong — R (reindex parcours)
  eventApprove: "#009E73",  // vert-bleu Wong — A (approbation en masse)
};
function EmbeddingsMiniChart({ snapshots, events = [] }) {
  // Tâche 1373 (Eric 2026-06-30) — Ce sous-composant a son propre hook :
  // il n'hérite PAS du `tr` de la page qui le contient.
  const { tr } = useLang();
  if (!snapshots || snapshots.length < 2) {
    return (
      <div className="text-xs text-muted-foreground italic text-center py-6 border border-dashed border-zinc-300 rounded">
        {trHorsContexte(
          "Pas encore assez de données pour afficher la courbe. La tâche quotidienne alimente cet historique — ou cliquez sur « Instantané maintenant » pour l'amorcer.",
          "Not enough data yet to draw the curve. The daily task feeds this history — or click “Snapshot now” to prime it.",
        )}
      </div>
    );
  }
  const W = 640;
  const H = 160;
  const pad = { top: 12, right: 12, bottom: 24, left: 42 };
  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;
  // Vincent B829 v2 follow-up — max sur les 4 séries pour que scripts_visible
  // (souvent 12/12 en preview, plus petit que KUs) reste lisible.
  const maxVal = Math.max(
    1,
    ...snapshots.map((s) => Math.max(
      s.total_approved || 0,
      s.scripts_total || 0,
      (s.visible_count || 0) + (s.missing_count || 0),
    )),
  );
  const xOf = (i) => pad.left + (i / Math.max(1, snapshots.length - 1)) * innerW;
  const yOf = (v) => pad.top + innerH - (v / maxVal) * innerH;
  const buildPath = (key) => snapshots.map((s, i) => `${i === 0 ? "M" : "L"} ${xOf(i).toFixed(1)} ${yOf(s[key] || 0).toFixed(1)}`).join(" ");
  const missingPath = buildPath("missing_count");
  const visiblePath = buildPath("visible_count");
  const scriptsMissingPath = buildPath("scripts_missing");
  const scriptsVisiblePath = buildPath("scripts_visible");
  // Vincent B829 v2 follow-up — Détection : y a-t-il au moins 1 snapshot
  // avec scripts_total > 0 ? Sinon on masque les 2 séries scripts (l'org
  // n'a pas encore de scripts de vente, aucun bruit à afficher).
  const hasScripts = snapshots.some((s) => (s.scripts_total || 0) > 0);
  const last = snapshots[snapshots.length - 1];
  const first = snapshots[0];
  const delta = (last.missing_count || 0) - (first.missing_count || 0);
  const deltaColor = delta > 0 ? "text-[#D55E00] font-semibold" : delta < 0 ? "text-[#0072B2] font-semibold" : "text-zinc-500";
  const deltaSign = delta > 0 ? "+" : "";
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5" style={{ backgroundColor: CB.missing }}></span>
          <span className="text-zinc-700">{tr("KU sans embedding", "Knowledge with no vector")}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5" style={{ backgroundColor: CB.visible }}></span>
          <span className="text-zinc-700">{tr("KU visibles chatbot", "Knowledge visible to the chatbot")}</span>
        </div>
        {hasScripts && (
          <>
            <div className="flex items-center gap-1" data-testid="chart-legend-scripts-missing">
              <span className="inline-block w-3 h-0.5" style={{ borderTop: `1px dashed ${CB.missing}`, backgroundColor: "transparent" }}></span>
              <span className="text-zinc-700">{tr("Scripts sans embedding", "Scripts with no vector")}</span>
            </div>
            <div className="flex items-center gap-1" data-testid="chart-legend-scripts-visible">
              <span className="inline-block w-3 h-0.5" style={{ borderTop: `1px dashed ${CB.visible}`, backgroundColor: "transparent" }}></span>
              <span className="text-zinc-700">{tr("Scripts visibles", "Visible scripts")}</span>
            </div>
          </>
        )}
        {/* Vincent B843 — Légende des annotations événements */}
        {events.length > 0 && (
          <>
            <div className="flex items-center gap-1" data-testid="chart-legend-event-backfill">
              <span className="inline-flex items-center justify-center w-3 h-3 rounded-full text-[8px] font-bold text-black" style={{ backgroundColor: CB.eventBackfill }}>B</span>
              <span className="text-zinc-700">Backfill</span>
            </div>
            <div className="flex items-center gap-1" data-testid="chart-legend-event-reindex">
              <span className="inline-flex items-center justify-center w-3 h-3 rounded-full text-[8px] font-bold text-black" style={{ backgroundColor: CB.eventReindex }}>R</span>
              <span className="text-zinc-700">{tr("Réindex parcours", "Pathway reindex")}</span>
            </div>
            {/* Vincent B844 — 3e type d'événement : approbation en masse */}
            <div className="flex items-center gap-1" data-testid="chart-legend-event-approve">
              <span className="inline-flex items-center justify-center w-3 h-3 rounded-full text-[8px] font-bold text-black" style={{ backgroundColor: CB.eventApprove }}>A</span>
              <span className="text-zinc-700">{tr("Approbation en masse", "Bulk approval")}</span>
            </div>
          </>
        )}
        <div className={`ml-auto font-semibold ${deltaColor}`} data-testid="emb-chart-delta">
          {deltaSign}{delta} {tr(`sur ${snapshots.length} jour${snapshots.length > 1 ? "s" : ""}`, `over ${snapshots.length} day${snapshots.length > 1 ? "s" : ""}`)}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="emb-mini-chart">
        {/* Y axis ticks (0, mid, max) */}
        {[0, 0.5, 1].map((t) => {
          const v = Math.round(maxVal * (1 - t));
          const y = pad.top + innerH * t;
          return (
            <g key={t}>
              <line x1={pad.left} y1={y} x2={W - pad.right} y2={y} stroke="#e4e4e7" strokeDasharray={t === 1 ? "0" : "2 3"} strokeWidth="1" />
              <text x={pad.left - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#71717a">{v}</text>
            </g>
          );
        })}
        {/* X axis labels : premier + milieu + dernier */}
        {[0, Math.floor(snapshots.length / 2), snapshots.length - 1].map((i) => (
          <text key={i} x={xOf(i)} y={H - 6} textAnchor="middle" fontSize="9" fill="#71717a">
            {snapshots[i]?.day_stamp?.slice(5) || ""}
          </text>
        ))}
        {/* Vincent B843 (Eric 2026-02-14 SPEC STRICK PROD) — Annotations
            événements admin : barre verticale pointillée + petit triangle
            en tête pour marquer un backfill ou un reindex-pathway.
            Couleur = orange pour backfill (opération globale), violet
            pour reindex-pathway (parcours). Tooltip = date + type +
            affected_count + pathway_name éventuel + actor_email. */}
        {events.map((ev) => {
          // Trouve l'index du snapshot le plus proche du day_stamp de l'event
          const dayIdx = snapshots.findIndex((s) => s.day_stamp === ev.day_stamp);
          if (dayIdx < 0) return null;
          const x = xOf(dayIdx);
          // Vincent B844 (Eric 2026-02-14 SPEC STRICK PROD) — 3e type
          // d'événement `bulk-approve` (approbation en masse). Couleur
          // verte + lettre A pour se distinguer visuellement de B/R.
          const evColor = {
            "backfill": CB.eventBackfill,       // 🟠 orange Wong
            "reindex-pathway": CB.eventReindex, // 🟣 mauve Wong
            "bulk-approve": CB.eventApprove,    // 🟢 vert-bleu Wong
          };
          const evLabel = {
            "backfill": "B",
            "reindex-pathway": "R",
            "bulk-approve": "A",
          };
          const color = evColor[ev.event_type] || "#64748b";
          const label = evLabel[ev.event_type] || "?";
          const humanType = ev.event_type === "backfill" ? "Backfill"
                          : ev.event_type === "reindex-pathway" ? tr("Réindex parcours", "Pathway reindex")
                          : ev.event_type === "bulk-approve" ? tr("Approbation en masse", "Bulk approval")
                          : ev.event_type;
          const tooltip = [
            `${ev.day_stamp} — ${humanType}`,
            `${tr("Affectées", "Affected")} : ${ev.affected_count ?? 0}`,
            ev.affected_embedded != null ? `${tr("Embeddées", "Vectorised")} : ${ev.affected_embedded}` : null,
            ev.pathway_name ? `${tr("Parcours", "Pathway")} : ${ev.pathway_name}` : null,
            ev.affected_ku != null ? `${tr("Connaissances", "Knowledge")} : ${ev.affected_ku}, Scripts : ${ev.affected_scripts ?? 0}` : null,
            ev.force_all ? `${tr("Mode", "Mode")} : force_all` : null,
            ev.actor_email ? `${tr("Par", "By")} : ${ev.actor_email}` : null,
          ].filter(Boolean).join("\n");
          return (
            <g key={ev.id} data-testid={`chart-event-${ev.event_type}`}>
              <title>{tooltip}</title>
              <line
                x1={x}
                y1={pad.top}
                x2={x}
                y2={pad.top + innerH}
                stroke={color}
                strokeWidth="1.5"
                strokeDasharray="3 3"
                opacity="0.65"
              />
              {/* Marqueur en tête (petit triangle) + lettre B/R/A */}
              <circle cx={x} cy={pad.top - 2} r="6" fill={color} />
              <text x={x} y={pad.top + 2} textAnchor="middle" fontSize="8" fontWeight="bold" fill="#ffffff">
                {label}
              </text>
            </g>
          );
        })}
        {/* Aire sous missing (KU) */}
        <path d={`${missingPath} L ${xOf(snapshots.length - 1)} ${pad.top + innerH} L ${pad.left} ${pad.top + innerH} Z`} fill={CB.missingArea} opacity="0.6" />
        {/* Lignes principales (KU) */}
        <path d={visiblePath} stroke={CB.visible} strokeWidth="2" fill="none" />
        <path d={missingPath} stroke={CB.missing} strokeWidth="2" fill="none" />
        {/* Vincent B829 v2 follow-up — Lignes scripts en pointillés */}
        {hasScripts && (
          <>
            <path d={scriptsVisiblePath} stroke={CB.visible} strokeWidth="1.5" strokeDasharray="4 3" fill="none" data-testid="chart-path-scripts-visible" />
            <path d={scriptsMissingPath} stroke={CB.missing} strokeWidth="1.5" strokeDasharray="4 3" fill="none" data-testid="chart-path-scripts-missing" />
          </>
        )}
        {/* Points */}
        {snapshots.map((s, i) => (
          <g key={i}>
            <circle cx={xOf(i)} cy={yOf(s.missing_count || 0)} r="3" fill={CB.missing}>
              <title>{s.day_stamp} — {tr("Connaissances sans vecteur", "Knowledge with no vector")} : {s.missing_count}</title>
            </circle>
            <circle cx={xOf(i)} cy={yOf(s.visible_count || 0)} r="3" fill={CB.visible}>
              <title>{s.day_stamp} — {tr("Connaissances visibles", "Visible knowledge")} : {s.visible_count}</title>
            </circle>
            {hasScripts && (
              <>
                <circle cx={xOf(i)} cy={yOf(s.scripts_missing || 0)} r="2.5" fill="#fff" stroke={CB.missing} strokeWidth="1.5">
                  <title>{s.day_stamp} — {tr("Scripts manquants", "Missing scripts")} : {s.scripts_missing ?? 0} / {s.scripts_total ?? 0}</title>
                </circle>
                <circle cx={xOf(i)} cy={yOf(s.scripts_visible || 0)} r="2.5" fill="#fff" stroke={CB.visible} strokeWidth="1.5">
                  <title>{s.day_stamp} — {tr("Scripts visibles", "Visible scripts")} : {s.scripts_visible ?? 0} / {s.scripts_total ?? 0}</title>
                </circle>
              </>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function EmbeddingsAdminPage() {
  const { lang, tr } = useLang();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [videoId, setVideoId] = useState("");
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  // Vincent B825 (Eric 2026-02-13) — Historique quotidien + bouton snapshot
  const [history, setHistory] = useState([]);
  // Vincent B843 (Eric 2026-02-14 SPEC STRICK PROD) — Annotations
  // événements admin (backfill / reindex-pathway) pour le mini-chart.
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyDays, setHistoryDays] = useState(30);
  const [snapshotting, setSnapshotting] = useState(false);

  const fetchHistory = async (days = historyDays) => {
    try {
      const r = await axios.get(`${API}/api/admin/embeddings-history?days=${days}`, { withCredentials: true });
      setHistory(r.data?.snapshots || []);
      setHistoryEvents(r.data?.events || []);
    } catch (e) {
      // Non-fatal — la carte affichera un état vide
    }
  };

  const takeSnapshot = async () => {
    setSnapshotting(true);
    try {
      const r = await axios.post(`${API}/api/admin/embeddings-snapshot`, {}, { withCredentials: true });
      toast.success(tr(
        `Instantané pris : ${r.data?.missing_count ?? 0} connaissances sans vecteur / ${r.data?.total_approved ?? 0} approuvées`,
        `Snapshot taken: ${r.data?.missing_count ?? 0} knowledge units with no vector / ${r.data?.total_approved ?? 0} approved`,
      ));
      await fetchHistory();
      await fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Erreur", "Error"));
    } finally {
      setSnapshotting(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/embeddings-stats`, { withCredentials: true });
      setStats(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Erreur", "Error"));
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => { fetchStats(); fetchHistory(); }, []);

  const runDiagnose = async () => {
    const vid = videoId.trim();
    if (!vid) { toast.error(tr("Identifiant de vidéo requis", "Video id required")); return; }
    setDiagLoading(true);
    setDiag(null);
    try {
      const r = await axios.get(`${API}/api/admin/knowledge/rag-diagnose?video_id=${encodeURIComponent(vid)}`, { withCredentials: true });
      setDiag(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Vidéo introuvable", "Video not found"));
    } finally {
      setDiagLoading(false);
    }
  };

  const runBackfill = async (forceAll = false) => {
    const msg = forceAll
      ? tr(
          "Réindexer TOUTES les connaissances de la plateforme (peut prendre plusieurs minutes selon le volume). Continuer ?",
          "Reindex EVERY knowledge unit on the platform (this can take several minutes depending on the volume). Continue?",
        )
      : tr(
          "Réindexer les connaissances sans vecteur ou avec une mauvaise dimension uniquement. Continuer ?",
          "Reindex only the knowledge units with no vector or with a wrong dimension. Continue?",
        );
    if (!(await confirmDialog(msg))) return;
    setBackfilling(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/backfill-embeddings${forceAll ? "?force_all=true" : ""}`,
        {},
        { withCredentials: true, timeout: 600000 }
      );
      toast.success(tr(
        `Réindexé : ${r.data?.processed ?? 0}/${r.data?.total ?? 0} connaissances`,
        `Reindexed: ${r.data?.processed ?? 0}/${r.data?.total ?? 0} knowledge units`,
      ));
      await fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Échec du backfill", "Backfill failed"));
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5 p-6" data-testid="embeddings-admin-page">
      <header className="flex items-center gap-3">
        <Cube size={30} weight="duotone" className="text-indigo-600" />
        <div>
          <h1 className="text-xl font-bold">Embeddings & RAG</h1>
          <p className="text-xs text-muted-foreground">{tr("Diagnostic et backfill des vecteurs pour l'index chatbot.", "Vector diagnostics and backfill for the chatbot index.")}</p>
        </div>
      </header>

      {/* Vincent B987 (Eric 2026-02-26 SPEC STRICK PROD) — Heartbeat cron
          embedding-health-check : vérifie en un coup d'œil que la tâche
          cron-job.org tourne bien tous les jours. Vert si run < 26h,
          ambre si 26-48h, rouge si > 48h ou jamais. */}
      <CronHeartbeatBanner heartbeat={stats?.cron_heartbeat} lang={lang} />

      {/* Stats globales */}
      <section className="bg-white border border-zinc-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-sm font-bold">{tr("Stats globales", "Overall stats")}</h2>
          <button
            onClick={fetchStats}
            disabled={statsLoading}
            className="ml-auto text-xs px-2 py-1 bg-zinc-100 hover:bg-zinc-200 rounded inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="emb-stats-refresh"
          >
            <ArrowsClockwise size={12} className={statsLoading ? "animate-spin" : ""} />
            {tr("Rafraîchir", "Refresh")}
          </button>
        </div>
        {stats ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <StatCard label={tr("Connaissances", "Knowledge")} value={stats.total} testid="total" />
            <StatCard label={tr("Dimension correcte (384)", "Correct dimension (384)")} value={stats.correct_dim} tone="good" testid="correct-dim" />
            <StatCard label={tr("À réindexer", "To reindex")} value={stats.needs_backfill} tone={stats.needs_backfill > 0 ? "bad" : "good"} testid="needs-backfill" />
            <StatCard label={tr("Dimension cible", "Target dimension")} value={stats.target_dim} testid="target-dim" />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">{tr("Chargement…", "Loading…")}</p>
        )}
        {stats?.distribution?.length > 0 && (
          <details className="mt-2">
            <summary className="text-xs cursor-pointer text-indigo-700 font-semibold">{tr("Voir la distribution par dimension", "See the distribution by dimension")}</summary>
            <table className="w-full text-xs mt-2">
              <thead className="bg-zinc-50 text-zinc-600">
                <tr><th className="text-left px-2 py-1">Dimension</th><th className="text-right px-2 py-1">{tr("Nombre", "Count")}</th></tr>
              </thead>
              <tbody>
                {stats.distribution.map((d) => (
                  <tr key={d.dim} className="border-t border-zinc-100">
                    <td className="px-2 py-1 font-mono">{d.dim}</td>
                    <td className="px-2 py-1 text-right">{d.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </section>

      {/* Vincent B825 (Eric 2026-02-13) — Mini-chart historique jour par jour */}
      <section className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="emb-history-section">
        <div className="flex items-center gap-2 mb-3">
          <ChartLine size={16} weight="duotone" className="text-indigo-600" />
          <h2 className="text-sm font-bold">{tr("Évolution jour par jour", "Day-by-day trend")}</h2>
          <select
            value={historyDays}
            onChange={(e) => { const d = parseInt(e.target.value, 10); setHistoryDays(d); fetchHistory(d); }}
            className="ml-auto text-xs px-2 py-1 border border-zinc-200 rounded bg-white"
            data-testid="emb-history-days-select"
          >
            <option value={7}>{tr("7 jours", "7 days")}</option>
            <option value={14}>{tr("14 jours", "14 days")}</option>
            <option value={30}>{tr("30 jours", "30 days")}</option>
            <option value={60}>{tr("60 jours", "60 days")}</option>
            <option value={90}>{tr("90 jours", "90 days")}</option>
          </select>
          <button
            onClick={takeSnapshot}
            disabled={snapshotting}
            className="text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="emb-history-snapshot-btn"
            title={tr("Force un snapshot maintenant (le cron quotidien en fait un par jour automatiquement)", "Force a snapshot now (the daily task takes one per day automatically)")}
          >
            <ArrowsClockwise size={11} className={snapshotting ? "animate-spin" : ""} />
            {snapshotting ? "…" : tr("Instantané maintenant", "Snapshot now")}
          </button>
        </div>
        <EmbeddingsMiniChart snapshots={history} events={historyEvents} />
      </section>

      {/* Backfill global (Vincent B823) */}
      <section className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
        <h2 className="text-sm font-bold mb-1 flex items-center gap-1.5">
          <Cube size={14} weight="duotone" className="text-indigo-700" />
          {tr("Réindexer tout le LMS", "Reindex the whole LMS")}
        </h2>
        <p className="text-xs text-indigo-900/80 mb-3">
          {tr(
            "Regénère les vecteurs de toutes les connaissances. Utile pour rattraper le stock historique approuvé sans vecteur. Par lots de 32, opération synchrone.",
            "Regenerates the vectors for every knowledge unit. Useful to catch up on the approved historical stock with no vector. In batches of 32, synchronous.",
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => runBackfill(false)}
            disabled={backfilling}
            className="px-3 py-2 text-xs font-semibold bg-indigo-600 text-white rounded hover:bg-indigo-700 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="emb-backfill-missing-btn"
          >
            <Cube size={13} weight="duotone" className={backfilling ? "animate-spin" : ""} />
            {backfilling ? tr("Réindexation…", "Reindexing…") : tr("Réindexer les manquants uniquement", "Reindex the missing ones only")}
          </button>
          <button
            onClick={() => runBackfill(true)}
            disabled={backfilling}
            className="px-3 py-2 text-xs font-semibold bg-rose-600 text-white rounded hover:bg-rose-700 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="emb-backfill-all-btn"
            title={tr("Force le recalcul de TOUS les embeddings — utile après changement de modèle sentence-transformers.", "Forces every vector to be recomputed — useful after changing the sentence-transformers model.")}
          >
            <Warning size={13} weight="duotone" />
            {backfilling ? tr("En cours…", "Running…") : tr("Réindexer TOUT le LMS (force)", "Reindex the WHOLE LMS (force)")}
          </button>
        </div>
      </section>

      {/* Diagnostic vidéo (B823) */}
      <section className="bg-white border border-zinc-200 rounded-xl p-4">
        <h2 className="text-sm font-bold mb-1 flex items-center gap-1.5">
          <MagnifyingGlass size={14} weight="duotone" className="text-indigo-700" />
          {tr("Diagnostiquer une vidéo", "Diagnose a video")}
        </h2>
        <p className="text-xs text-muted-foreground mb-2">
          {tr("Colle un video_id pour voir pourquoi ses KU ne remontent pas dans le chatbot.", "Paste a video_id to see why its knowledge does not surface in the chatbot.")}
        </p>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={videoId}
            onChange={(e) => setVideoId(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runDiagnose(); }}
            placeholder="video_id (uuid)"
            className="flex-1 min-w-0 px-3 py-2 text-sm border border-zinc-300 rounded font-mono"
            data-testid="emb-diagnose-input"
          />
          <button
            onClick={runDiagnose}
            disabled={diagLoading || !videoId.trim()}
            className="px-3 py-2 text-xs font-semibold bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            data-testid="emb-diagnose-btn"
          >
            {diagLoading ? "…" : tr("Diagnostiquer", "Diagnose")}
          </button>
        </div>
        {diag && (
          <div className="border-t border-zinc-100 pt-3 space-y-3" data-testid="emb-diagnose-result">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold">{diag.video.title}</span>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">{diag.video.pipeline_status}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <StatCard label={tr("Connaissances", "Knowledge")} value={diag.total_kus} testid="diag-total" />
              <StatCard label={tr("Visible au chatbot", "Visible to the chatbot")} value={diag.visible_to_rag} tone="good" testid="diag-visible" />
              <StatCard label={tr("En attente de revue", "Pending review")} value={diag.pending_review_count} tone={diag.pending_review_count > 0 ? "warn" : "default"} testid="diag-pending" />
              <StatCard label={tr("Sans embedding", "No vector")} value={diag.no_embedding_count} tone={diag.no_embedding_count > 0 ? "bad" : "good"} testid="diag-no-vector" />
            </div>
            {diag.recommendations?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2">
                <div className="text-[10px] uppercase tracking-wide font-bold text-amber-800 mb-1 flex items-center gap-1">
                  <Warning size={11} weight="fill" /> {tr("Recommandations", "Recommendations")}
                </div>
                <ul className="text-xs text-amber-900 space-y-1">
                  {diag.recommendations.map((r, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <span className="text-amber-600">•</span> <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {diag.recommendations?.length === 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center gap-2 text-xs text-emerald-800">
                <CheckCircle size={13} weight="fill" /> {tr("Aucun problème détecté sur cette vidéo.", "No problem found on this video.")}
              </div>
            )}
            <details className="text-xs">
              <summary className="cursor-pointer text-indigo-700 font-semibold">{tr("Voir la répartition par statut / type", "See the split by status / type")}</summary>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wide font-bold text-zinc-500 mb-1">{tr("Par statut", "By status")}</div>
                  <ul className="space-y-0.5">
                    {Object.entries(diag.by_status || {}).map(([k, v]) => (
                      <li key={k} className="flex justify-between"><span className="text-zinc-700">{k}</span><span className="font-mono">{v}</span></li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide font-bold text-zinc-500 mb-1">{tr("Par type", "By type")}</div>
                  <ul className="space-y-0.5">
                    {Object.entries(diag.by_type || {}).map(([k, v]) => (
                      <li key={k} className="flex justify-between"><span className="text-zinc-700">{k}</span><span className="font-mono">{v}</span></li>
                    ))}
                  </ul>
                </div>
              </div>
            </details>
          </div>
        )}
      </section>
    </div>
  );
}
